import type { EGoalType, EGoalTypeV1, IGoal } from '@feathr/blackbox';
import type { ISorted } from '@feathr/components';

export interface IGoalRow {
  id: string;
  name: string;
  type: EGoalType | EGoalTypeV1;
  target: string;
  conversions: number;
  conversionValue: number;
  isConversionSegment: boolean;
}

interface IGoalStats {
  goal_id: string;
  num: number;
  roi: number;
}

interface ISegmentStats {
  goal_segment_id: string;
  goals?: IGoalStats[];
}

export interface IConversionStats {
  segments_breakdown?: ISegmentStats[];
}

export const goalTypeLabels: Record<EGoalType | EGoalTypeV1, string> = {
  page_view: 'URL visit',
  group_entry: 'Group entry',
  conversion_pixel: 'Conversion pixel trigger',
  donation: 'Donation form submission',
  form_submission: 'Form submission',
  ad_click: 'Group entry',
  custom: 'Group entry',
};

export function transformGoalsToRows(
  goals: IGoal[],
  conversionStats: IConversionStats | undefined,
  conversionSegmentBySegmentId: ReadonlyMap<string, boolean>,
): IGoalRow[] {
  return goals.map((goal) => {
    const segmentStats = conversionStats?.segments_breakdown?.find(
      (s) => s.goal_segment_id === goal.segment,
    );
    const goalStats = segmentStats?.goals?.find((g) => g.goal_id === goal.id);
    const isConversionSegment = goal.segment
      ? conversionSegmentBySegmentId.get(goal.segment) ?? false
      : false;
    return {
      id: goal.id!,
      name: goal.name,
      type: goal.conv_type,
      target: goal.segment || '',
      conversions: goalStats?.num ?? 0,
      conversionValue: goal.conv_value ?? 0,
      isConversionSegment,
    };
  });
}

export function sortGoalsData(data: IGoalRow[], sort: ISorted[]): IGoalRow[] {
  const sortConfig = sort[0];
  if (!sortConfig) {
    return data;
  }

  return [...data].sort((a, b) => {
    const aVal = a[sortConfig.id as keyof IGoalRow];
    const bVal = b[sortConfig.id as keyof IGoalRow];

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortConfig.desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortConfig.desc ? bVal - aVal : aVal - bVal;
    }
    return 0;
  });
}
export interface IPaginationResult<T> {
  paginatedData: T[];
  totalPages: number;
}

export function paginateData<T>(data: T[], page: number, pageSize: number): IPaginationResult<T> {
  return {
    paginatedData: data.slice(page * pageSize, (page + 1) * pageSize),
    totalPages: Math.ceil(data.length / pageSize),
  };
}
